import MaterialTable from '@material-table/core';
import { Tooltip, Box, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { AppConfig } from 'AppConfig';
import axios from 'axios';
import { confirmDialog } from 'common/ComfirmationDialog';
import * as Constant from 'common/Constant';
import * as UserTypes from 'features/user/userTypes';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { store } from 'store/store';
import * as Actions from './actions/action';
import LogoImg from './components/logoImg';
import LogoUploaderDialog from './components/logoUploaderDialog';

// Wrap SearchIcon with forwardRef for MaterialTable compatibility
const SearchIconWithRef = React.forwardRef((props, ref) => (
  <SearchIcon {...props} ref={ref} color="primary" />
));

const WhiteLabelingManagement = () => {
  const tableRef = useRef();

  const [open, setOpen] = useState(false);
  const [targetData, setTargetData] = useState(false);
  const [pageSize, setPageSize] = useState(Constant.DEFAULT_SELECT_PAGE_SIZE);

  const shouldRefresh = useSelector((state) => state.whiteLabeling.shouldRefresh);

  const handleLogoUploaderOpen = (rowData) => {
    setTargetData(rowData);
    setOpen(true);
  };

  const handleSave = (data) => {
    const lightLogoFile = data?.lightLogoFile;
    const darkLogoFile = data?.darkLogoFile;
    if (data && ((lightLogoFile && lightLogoFile instanceof File)
      || (darkLogoFile && darkLogoFile instanceof File))) {

      if (data.TYPE === Constant.LOGO_TENANT_LOGO) {
        store.dispatch(Actions.uploadTenantLogo(data.code, lightLogoFile, darkLogoFile));
      } else {
        store.dispatch(Actions.uploadDefaultLogo(lightLogoFile, darkLogoFile));
      }
    }
  };

  const handleLogoUploaderClose = (data) => {
    handleSave(data);
    setOpen(false);
  };

  const deleteLogo = (tenantCode) => {
    confirmDialog(
      () => store.dispatch(Actions.deleteLogo(tenantCode)),
      'Are you sure you want to remove the logos (System will use default logos instead)?'
    );
  };

  const deleteLogoByType = (tenantCode, logoType) => {
    const logoNumber = logoType === 'light' ? 1 : 2;
    const otherLogoNumber = logoType === 'light' ? 2 : 1;
    confirmDialog(
      () => store.dispatch(Actions.deleteTenantLogoByType(tenantCode, logoType)),
      `Are you sure you want to remove Logo ${logoNumber}? The system will use the default logo instead. Logo ${otherLogoNumber} will not be affected.`
    );
  };

  const refresh = () => {
    tableRef.current?.onQueryChange();
  };

  useEffect(() => {
    store.dispatch({
      type: UserTypes.APP_SWITCHED,
      payload: AppConfig.HEADER_MENU_TYPE.NONE
    });
    refresh();
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (shouldRefresh !== Actions.SHOULD_REFRESH_INIT_VALUE) {
      refresh();
    }
  }, [shouldRefresh]);

  return (
    <>
      <MaterialTable
        title="Logo Management"
        tableRef={tableRef}
        localization={{
          toolbar: { searchPlaceholder: 'Search for Name or Code' }
        }}
        columns={[
          { title: 'Name', field: 'name' },
          { title: 'Code', field: 'code' },
          { title: 'Broker Name', field: 'brokerName' },
          {
            title: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box component="span">Logo 1</Box>
                <Tooltip title="Light background logo. Used in Tableau reports.">
                  <InfoOutlinedIcon
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { color: 'rgba(0, 0, 0, 0.54)' }
                    }}
                  />
                </Tooltip>
              </Box>
            ),
            field: 'brokerName',
            render: (rowData) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <LogoImg data={rowData} url={AppConfig.FO_INITIAL_URL + '/open-api/logo/v2/tenant/'} variant="light" />
                <Tooltip title="Remove Logo 1">
                  <IconButton 
                    onClick={() => deleteLogoByType(rowData.code, 'light')}
                    sx={{ color: 'rgba(0, 0, 0, 0.87)' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )
          },
          {
            title: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box component="span">Logo 2</Box>
                <Tooltip title="Dark background logo. Used in the left navigation menu.">
                  <InfoOutlinedIcon
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { color: 'rgba(0, 0, 0, 0.54)' }
                    }}
                  />
                </Tooltip>
              </Box>
            ),
            field: 'brokerName',
            cellStyle: {
              backgroundColor: Constant.LEFT_NAV_DARK_BLUE
            },
            render: (rowData) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <LogoImg data={rowData} url={AppConfig.FO_INITIAL_URL + '/open-api/logo/v2/tenant/'} variant="dark" />
              </Box>
            )
          }
        ]}
        data={(query) =>
          new Promise((resolve, reject) => {
            axios
              .post(AppConfig.FO_INITIAL_URL + '/company-admin/v2/search', {
                search: query.search,
                page: query.page,
                size: query.pageSize,
                direction: query.orderDirection
                  ? query.orderDirection.toUpperCase()
                  : Constant.DIRECTION_ASC,
                sortProperty: query.orderBy ? query.orderBy.field : 'name'
              })
              .then((m) => {
                let data = m.data;
                resolve({
                  data: data.content,
                  page: data.number,
                  size: data.szie,
                  totalCount: data.totalElements
                });
              })
              .catch((err) => {
                resolve({
                  data: [],
                  page: 0,
                  totalCount: 0
                });
              });
          })
        }
        onChangeRowsPerPage={setPageSize}
        options={{
          selection: false,
          actionsColumnIndex: -1,
          search: true,
          exportButton: false,
          pageSizeOptions: Constant.DEFAULT_SEARCH_PAGE_SIZE,
          debounceInterval: 400,
          pageSize
        }}
        components={{ OverlayLoading: () => <div /> }}
        actions={[
          {
            icon: () => <RefreshIcon />,
            tooltip: 'Refresh Data',
            onClick: () => {
              refresh();
            },
            isFreeAction: true
          },
          {
            position: 'row',
            icon: () => <EditIcon />,
            tooltip: 'Edit',
            onClick: (_, rowData) => {
              handleLogoUploaderOpen({
                ...rowData,
                TYPE: Constant.LOGO_TENANT_LOGO
              });
            }
          },
          {
            icon: () => <DeleteIcon />,
            tooltip: 'Remove Logo 2',
            onClick: (_, rowData) => deleteLogoByType(rowData.code, 'dark')
          },
          {
            position: 'row',
            icon: () => <AddIcon />,
            tooltip: 'Add',
            isFreeAction: true,
            onClick: () => {
              handleLogoUploaderOpen({ TYPE: Constant.LOGO_DEFAULT_TYPE });
            }
          }
        ]}
        icons={{
          Search: SearchIconWithRef,
        }}
      />
      <LogoUploaderDialog
        open={open}
        onClose={handleLogoUploaderClose}
        target={targetData}
      />
    </>
  );
};

export default WhiteLabelingManagement;
